import { useQuery } from '@apollo/client/react';
import { ArrowUpTrayIcon, CloudArrowDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { BalanceOverview } from '../../components/CreditLedger/BalanceOverview';
import { Tooltip } from '../../components/Tooltip';
import { ChangeEventHandler, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/Button';
import { useStatusTranslations } from '../../components/Pill';
import { UploadCard } from '../../components/UploadCard';
import { NavVisCard } from '../../components/UploadCard/NavVisCard';
import { Option, Select, SelectProps } from '../../components/inputs/Select';
import { UserContext } from '../../contexts/UserContext';
import { USERS } from '../../graphql/user';
import { usePointCloudDrop, usePointCloudDropWithModals } from '../../hooks/modules/project/usePointCloudDrop';
import { useRecentProjects } from '../../hooks/modules/project/useRecentProjects';
import { useOrganisationProjectEvents } from '../../hooks/modules/project/useOrganisationProjectEvents';
import { useNavVis } from '../../hooks/modules/navvis/useNavVis';
import useOpen from '../../hooks/useOpen';
import useQueryParams from '../../hooks/useQueryParams';
import { LazyProjectCard } from '../../modules/project/ProjectCard/LazyProjectCard';
import { NoCreditsBanner } from '../../modules/organisation/NoCreditsBanner';
import { ShouldUpgradeModal } from '../../modules/user/ShouldUpgradeModal';
import { T, useT } from '../../translation/src';
import { Permissions, UploadStatus, UserRole, UsersQuery, UsersQueryVariables, useOrganisationsQuery, useProjectsQuery } from '../../types/graphqlTypes';
import { useDeviceSize } from '../../hooks/useDeviceSize';
import classNames from 'classnames';
import { PageContent } from '../../components/page/PageContent';
import { PageHeader } from '../../components/page/PageHeader';
import { Popover, PopoverListItem } from '../../components/Popover';
import { Input } from '../../components/inputs/Input';

const useTranslations = () => {
  const shouldUpgradeModalDescriptionUpload = useT('In order to add more simultaneous uploads, you need to upgrade your plan.');
  const searchProjects = useT('search projects', { swc: true });
  return { shouldUpgradeModalDescriptionUpload, searchProjects };
};

export const Projects: React.FC = () => {
  const { organisationId = '' } = useParams<{ organisationId: string }>();
  const { projectsSelect } = useQueryParams(['projectsSelect']);
  const projectsQuery = useProjectsQuery({ variables: { organisationId } });
  // Project list updates flow through Pusher (useOrganisationProjectEvents below).
  // No polling: a manual refresh is the fallback if a Pusher event is missed.
  useOrganisationProjectEvents({
    organisationId,
    onProjectUpdated: () => projectsQuery.refetch().catch((e) => console.error('refetch projects failed:', e)),
  });
  const { open: shouldUpgradeModalOpen, onClose: closeShouldUpgradeModal, onOpenWithValue: openShouldUpgradeModal, value: shouldUpgradeModalDescription } = useOpen<string>();

  const usersQuery = useQuery<UsersQuery, UsersQueryVariables>(USERS, { variables: { organisationId } });
  const translations = useTranslations();
  const users = usersQuery.data?.usersByOrganisationId || [];
  const statusTranslations = useStatusTranslations();
  const [filters, setFilters] = useState({ name: '', status: 'ALL', assigneeId: 'ALL' });
  const [recentProjects] = useRecentProjects();
  const currentUser = useContext(UserContext);
  const { data: organisations } = useOrganisationsQuery(currentUser ? { variables: { includeIds: [organisationId] } } : {});
  const [{ modals: uploadModals }, { onNewFiles }] = usePointCloudDropWithModals();
  usePointCloudDrop({ onNewFiles });
  const { modals: navVisModals, openLoginModal: openNavVisLogin } = useNavVis();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUploadFile = useCallback(() => {
    // Use setTimeout to allow the popover to close first before opening file dialog
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 0);
  }, []);
  const { smDevice } = useDeviceSize();
  const orderedUploadStatus = [UploadStatus.Progress, UploadStatus.AwaitingCredits, UploadStatus.Success, UploadStatus.Suspended, UploadStatus.Error];

  const projects = useMemo(() => {
    return (projectsQuery.data?.projectsByOrganisationId || [])
      .filter((project) => {
        if (project.isInitializing) return false;
        if (filters.name && !project.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
        if (filters.status !== 'ALL') {
          if (filters.status !== project.uploadStatus) return false;
        }
        if (filters.assigneeId !== 'ALL') {
          if (filters.assigneeId !== project.assignee?.id) return false;
        }
        if (projectsSelect === 'recent') {
          if (!recentProjects.includes(project.id)) return false;
        }
        if (projectsSelect === 'favorites') {
          if (!currentUser.favoriteProjectIds.includes(project.id)) return false;
        }
        return true;
      })
      .reverse();
  }, [filters.name, filters.assigneeId, filters.status, projectsQuery.data?.projectsByOrganisationId, projectsSelect, recentProjects, currentUser]);

  const onFilter: SelectProps<string | number>['onChange'] = useCallback((event) => {
    setFilters((filters) => ({ ...filters, [event.target.name]: event.target.value }));
  }, []);

  const organisation = organisations?.organisations?.find((organisation) => organisation.id === organisationId);

  // A partner's admin on a referred organisation: the list of projects and a relaunch, nothing else.
  const partnerScoped = !currentUser.isSuperAdmin && (organisation?.myPermissions ?? []).includes(Permissions.Partner);
  const isReadOnly = !currentUser.isSuperAdmin && currentUser.rolesByOrganisation.find((value) => value.organisationId === organisationId)?.role === UserRole.Guest;
  // Billed to a partner, the organisation buys nothing itself: its partner does.
  const canBuyCredits = !organisation?.referral.billedTo && (currentUser.isSuperAdmin || (organisation?.myPermissions || []).some((permission) => permission === Permissions.All || permission === Permissions.Organisations));
  // Uploads are gated on available credits; superadmins bypass the gate.
  const outOfCredits = !!organisation && !currentUser.isSuperAdmin && !isReadOnly && organisation.credits.available < 1;
  // Paused (from Odoo): uploads wait, whatever the balance; superadmins bypass this gate too.
  const paused = !!organisation && !currentUser.isSuperAdmin && !isReadOnly && organisation.subscription.paused;

  // What activating a parked project can draw on, for its card: the balance (what the charge takes), none while paused.
  const projectsAvailable = organisation ? (organisation.subscription.paused ? 0 : organisation.credits.balance) : undefined;

  // What adding a project draws on, on hover over anything that adds one.
  const balance = useMemo(() => (organisation ? <BalanceOverview available={organisation.credits.available} /> : <span />), [organisation]);

  const addProjectButton = useMemo(() => {
    // Out of projects or paused: no button, the banner below explains why.
    if (!organisation || isReadOnly || partnerScoped || outOfCredits || paused) return;
    if ((organisation.credits.available >= 1 && organisation.subscription.canAddSimultaneousUploads && !organisation.subscription.paused) || currentUser.isSuperAdmin) {
      return (
        <>
          <input
            type="file"
            className="sr-only"
            ref={fileInputRef}
            onChange={(e) => {
              const files = e.target.files;
              if (files?.length) {
                onNewFiles(Array.from(files));
              }
              e.target.value = '';
            }}
            multiple
            accept=".las,.pts,.laz,.e57"
          />
          <Popover
            buttonChild={
              <Tooltip message={balance} placement="bottom" className="p-3" followMouse>
                <Button variant="primary">
                  <PlusIcon className={smDevice ? 'w-5' : 'w-5 mr-2'} />
                  {!smDevice && <T _str="add new project" swc />}
                </Button>
              </Tooltip>
            }
          >
            <PopoverListItem icon={ArrowUpTrayIcon} onClick={handleUploadFile}>
              <T _str="Upload file" swc />
            </PopoverListItem>
            <PopoverListItem icon={CloudArrowDownIcon} onClick={openNavVisLogin}>
              <T _str="Load NavVis IVION" swc />
            </PopoverListItem>
          </Popover>
        </>
      );
    }
    // Only a full set of simultaneous uploads gets here.
    const blockedReason = translations.shouldUpgradeModalDescriptionUpload;
    return (
      <Tooltip message={<BalanceOverview available={organisation.credits.available} blockedReason={blockedReason} />} placement="bottom" className="p-3" followMouse>
        <Button variant="primary" onClick={() => openShouldUpgradeModal(blockedReason)}>
          <PlusIcon className={smDevice ? 'w-5' : 'w-5 mr-2'} />
          {!smDevice && <T _str="add new project" swc />}
        </Button>
      </Tooltip>
    );
  }, [
    balance,
    organisation,
    isReadOnly,
    partnerScoped,
    currentUser.isSuperAdmin,
    smDevice,
    onNewFiles,
    handleUploadFile,
    openNavVisLogin,
    openShouldUpgradeModal,
    outOfCredits,
    paused,
    translations.shouldUpgradeModalDescriptionUpload,
  ]);

  const onChangeFilterName: ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    setFilters((filters) => ({ ...filters, name: event.target.value }));
  }, []);

  const pageHeaderTitle = useMemo(() => {
    if (projectsSelect === 'recent') {
      return <T _str="recent projects" swc />;
    }
    if (projectsSelect === 'favorites') {
      return <T _str="favorite projects" swc />;
    }
    if (!projectsSelect || projectsSelect === 'all') {
      return <T _str="all projects" swc />;
    }
    return null;
  }, [projectsSelect]);

  return (
    <>
      <PageContent>
        <div className={smDevice ? 'relative z-20 pt-4 text-xl' : 'relative z-20 pt-10 text-xl'}>
          <PageHeader title={pageHeaderTitle} button={addProjectButton} />
          {(outOfCredits || paused) && (
            <NoCreditsBanner
              paused={paused}
              className="mt-6"
              organisationId={organisationId}
              balance={organisation.credits.balance}
              projectUnitPrice={organisation.subscription.projectUnitPrice}
              minPurchaseAmount={organisation.subscription.minPurchaseAmount}
              maxPurchaseAmount={organisation.subscription.maxPurchaseAmount}
              canBuyCredits={canBuyCredits}
            />
          )}
          {!smDevice && (
            <div className="flex space-x-4 mt-14">
              <Select onChange={onFilter} value={filters.status} name="status" isLabelPlaceholder placeholder={<T _str="status" swc />} className="w-48">
                {['ALL', ...Object.values(orderedUploadStatus)].map((status) => (
                  <Option key={status} value={status}>
                    {statusTranslations[status.toLocaleLowerCase()] || <T _str="all" swc />}
                  </Option>
                ))}
              </Select>
              <Select onChange={onFilter} value={filters.assigneeId} name="assigneeId" isLabelPlaceholder placeholder={<T _str="uploaded by" swc />} className="w-72">
                {[{ id: 'ALL', name: '' }, ...users].map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.name || <T _str="all" swc />}
                  </Option>
                ))}
              </Select>
              <Input className="w-72" placeholder={`${translations.searchProjects}...`} value={filters.name} onChange={onChangeFilterName} />
            </div>
          )}
        </div>

        <div className={classNames('w-full h-full overflow-auto', smDevice ? 'mt-4' : 'mt-10')}>
          <div
            className={classNames(
              'grid w-full gap-8 rounded-md grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 4xl:grid-cols-6 5xl:grid-cols-7 6xl:grid-cols-8 7xl:grid-cols-9 8xl:grid-cols-10 9xl:grid-cols-11 10xl:grid-cols-12 4k:grid-cols-13',
            )}
          >
            {((organisation?.credits.available ?? 0) >= 1 && organisation?.subscription.canAddSimultaneousUploads && !isReadOnly && !partnerScoped) || currentUser.isSuperAdmin ? (
              <>
                {/* Each card fills its cell as it did without the tooltip around it. */}
                <Tooltip message={balance} placement="bottom" className="p-3" followMouse wrapperClassName="flex h-full [&>*]:w-full">
                  <UploadCard />
                </Tooltip>
                <Tooltip message={balance} placement="bottom" className="p-3" followMouse wrapperClassName="flex h-full [&>*]:w-full">
                  <NavVisCard onClick={openNavVisLogin} />
                </Tooltip>
                {currentUser.isSuperAdmin && (
                  <Tooltip message={balance} placement="bottom" className="p-3" followMouse wrapperClassName="flex h-full [&>*]:w-full">
                    <UploadCard isPythagorasFile isSuperAdmin />
                  </Tooltip>
                )}
              </>
            ) : null}
            {projects.map((project) => (
              <LazyProjectCard key={project.id} project={project} isReadOnly={isReadOnly} partnerScoped={partnerScoped} projectsAvailable={projectsAvailable} />
            ))}
          </div>
        </div>
      </PageContent>
      {uploadModals}
      {navVisModals}
      <ShouldUpgradeModal open={shouldUpgradeModalOpen} onClose={closeShouldUpgradeModal} description={shouldUpgradeModalDescription || ''} />
    </>
  );
};
